import {BottomTabNavigationProp, BottomTabScreenProps} from '@react-navigation/bottom-tabs';
import {DrawerScreenProps} from '@react-navigation/drawer';
import {CompositeScreenProps, NavigatorScreenParams} from '@react-navigation/native';
import {StackScreenProps} from '@react-navigation/stack';

import type {DepartmentJoinId} from '../components/SiteHeader/menu/departmentJoins';
import {type GiftCardShopStackParamList} from '../giftCardShop/types/navigationTypes';
import {AccountStatus} from '../screens/rewards/types';
import {OrderLine} from '../transport/apiServiceResponse';
import type {StaffConflictKind} from '../utils/staffActivation';

import {
  AirtableBrand,
  AirtableFeedItem,
  AirtableFeedItemStub,
  AirtablePromotionItem,
} from './airtable';
import {AppRoute} from './AppRoute';
import {Department} from './middleware';

// Navigator hierarchy types (params and props)

// Root stack, auth (store selection) flow
export type RootStackParamList = {
  [AppRoute.ROOT]: undefined;
  [AppRoute.ROOT_STORE_SELECTION_FLOW]: undefined;
  [AppRoute.ROOT_REWARDS_FLOW]: undefined;
  [AppRoute.ROOT_PROMOTIONS_FLOW]: undefined;
  [AppRoute.ROOT_CHECKOUT_FLOW]: undefined;
  [AppRoute.ROOT_STORYBOOK]: undefined;
  [AppRoute.ROOT_APP_BUILD_INFO]: undefined;
  [AppRoute.SUMMER_CASH_GRAB_GAME]: PromotionIframeParams;
};

export type RootStackScreenProps<RouteT extends keyof RootStackParamList> = StackScreenProps<
  RootStackParamList,
  RouteT
>;

// Drawer
export type DrawerParamList = {
  [AppRoute.MAIN]: NavigatorScreenParams<AppRoutesNavigatorParamList>;
};

// Drawer "window" (for programmatic navigation inside the drawer)
export type DrawerNavigatorScreenProps = CompositeScreenProps<
  StackScreenProps<RootStackParamList, AppRoute.ROOT>,
  DrawerScreenProps<DrawerParamList>
>;

// Root store selection flow
export type RootStoreSelectionFlowParamList = {
  [AppRoute.MY_STORE_SUBURB_SELECTION]: undefined;
  [AppRoute.MY_STORE_STORE_SELECTION]: undefined;
  [AppRoute.ROOT_GIFT_CARDS_PROMOTION]: undefined;
  [AppRoute.ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS]: undefined;
};

export type RootRewardsFlowParamList = {
  [AppRoute.REWARDS_SIGN_IN]: {staff?: string; signUpCode?: string; lmgCode?: string} | undefined;
  [AppRoute.REWARDS_STAFF_STORE_SELECTION]: RewardsSignUpTypeParams;
  [AppRoute.REWARDS_LMG_STAFF_SELECTION]: RewardsSignUpTypeParams;
  [AppRoute.REWARDS_SIGN_UP]: RewardsSignUpTypeParams;
  [AppRoute.REWARDS_VERIFICATION]: RewardsSignUpTypeParams | undefined;
  [AppRoute.REWARDS_SUCCESS]: undefined;
  [AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED]: {conflictKind?: StaffConflictKind} | undefined;
  [AppRoute.REWARDS_ACCOUNT_NOT_FOUND]: undefined;
  [AppRoute.REWARDS_TERMS_AND_CONDITIONS]: undefined;
  [AppRoute.REWARDS_FULL_SIGN_UP]: RewardsFullSignUpParams;
  [AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS]: RewardsActivateTermsAndConditionsParams;
  [AppRoute.REWARDS_ACTIVATE_VERIFICATION]: RewardsActivateVerificationParams;
  [AppRoute.REWARDS_REDIRECT]: undefined;
  [AppRoute.REWARDS_ACTIVATE_SUCCESS]: RewardsActivateSuccessParams;
};

export type RootPromotionsFlowParamList = {
  [AppRoute.PROMOTIONS_OPT_OUT]: CompetitionOptOutParams;
};

export type RootCheckoutFlowParamList = {
  [AppRoute.CART_ORDER_CHECKOUT]: undefined;
  [AppRoute.CART_ORDER_CHECKOUT_REVIEW]: undefined;
  [AppRoute.CART_ORDER_CHECKOUT_PAYMENT]: undefined;
  [AppRoute.CART_ORDER_PROCESSING]: undefined;
  [AppRoute.CART_ORDER_COMPETITIONS]: undefined;
  [AppRoute.CART_ORDER_CONFIRMATION]: undefined;
};

// Storybook routes
export type RootStorybookParamList = {
  [AppRoute.STORYBOOK]: undefined;
};

// Stack selection stack "window" doesn't need props definitions as we don't use
// its props for `navigation` or `route`

// Root store selection screen stack navigator screen props
export type RootStoreSelectionNavigatorScreenProps<
  RouteT extends keyof RootStoreSelectionFlowParamList,
> = CompositeScreenProps<
  StackScreenProps<RootStoreSelectionFlowParamList, RouteT>,
  StackScreenProps<RootStackParamList>
>;

// Main app navigator, bottom tabs on mobile, stack on desktop
export type AppRoutesNavigatorParamList = {
  // define the navigation props and its values
  // if no values are needed, pass undefined - https://reactnavigation.org/docs/typescript/
  [AppRoute.HOME]: {openSuburbModal?: boolean} | undefined;
  [AppRoute.SHOP]: undefined;
  [AppRoute.DISCOVER]: undefined;
  [AppRoute.CATALOGUE]: undefined;
  [AppRoute.CART]: undefined;
  // Don't need FF for types
  [AppRoute.REWARDS]: undefined;

  [AppRoute.EXCLUSIVE_OFFER_LIST]: undefined;
  [AppRoute.MEMBERS_PRICE_LIST]: MembersPriceListParams;

  [AppRoute.MY_STORE]: undefined;
  [AppRoute.ACCOUNT]: undefined;
  [AppRoute.WISHLIST]: undefined;
  [AppRoute.PROMOTIONS]: undefined;
  [AppRoute.CONTACT_US]: undefined;
  [AppRoute.INFORMATION]: undefined;
  [AppRoute.RESPONSIBLE_SERVICE]: undefined;
  [AppRoute.SELF_EXCLUSION]: undefined;

  [AppRoute.NEW_SEARCH]: {keywords?: string} | undefined;

  [AppRoute.ARTICLE]: ArticleDetailParams;
  [AppRoute.PROMOTION]: PromotionDetailParams;
  [AppRoute.EXCLUSIVE_OFFER_DETAIL]: ExclusiveOfferDetailParams;
  [AppRoute.PRODUCT]: ProductParams;
  [AppRoute.SUB_DEPARTMENT]: SubDepartmentParams;
  [AppRoute.DEPARTMENT]: DepartmentParams;
  [AppRoute.SHOP_ALL]: ShopAllParams;
  [AppRoute.FAVOURITE_BRANDS]: undefined;
  [AppRoute.BRAND]: ShopBrandParams;
  [AppRoute.CART_ORDER_CHECKOUT]: undefined;
  [AppRoute.CART_ORDER_CHECKOUT_REVIEW]: undefined;
  [AppRoute.CART_ORDER_CHECKOUT_PAYMENT]: undefined;
  [AppRoute.CART_ORDER_PROCESSING]: undefined;
  [AppRoute.CART_ORDER_COMPETITIONS]: undefined;
  [AppRoute.CART_ORDER_CONFIRMATION]: undefined;
  [AppRoute.PROMOTIONS_CURRENT]: undefined;
  [AppRoute.PROMOTIONS_WINNERS]: undefined;
  [AppRoute.PROMOTION_IFRAME]: PromotionIframeParams;
  [AppRoute.PROMOTION_WINNERS_DETAIL]: PromotionsWinnersDetailParams;
  [AppRoute.PROMOTIONS_OPT_OUT]: CompetitionOptOutParams;
  [AppRoute.CONTACT_US_SUBMIT_TICKET]: {prefill?: TicketPrefill} | undefined;
  [AppRoute.INFORMATION_CONTENT]: InformationContentParams | undefined;
  [AppRoute.ACCOUNT_DELETE_INFO]: undefined;
  [AppRoute.RESPONSIBLE_SERVICE_LIQUOR_LICENSING_LAWS]: undefined;
  [AppRoute.RESPONSIBLE_SERVICE_THIRD_PARTY_REQUEST]: undefined;

  [AppRoute.THREEDS_IFRAME_FULLSCREEN]: IFrameFullScreen3DSParams;

  // Store
  [AppRoute.MY_STORE_SUBURB_SELECTION]: undefined;
  [AppRoute.MY_STORE_STORE_SELECTION]: undefined;
  [AppRoute.STORE_SWITCH_LINK]: StoreSwitchLinkParams;

  // Account
  [AppRoute.ACCOUNT_LOGGED_IN]: undefined;
  [AppRoute.ACCOUNT_NOT_LOGGED_IN]: undefined;

  [AppRoute.ACCOUNT_LOGIN]: AccountLoginParams | undefined;
  [AppRoute.ACCOUNT_CREATE]: AccountCreateParams;
  [AppRoute.ACCOUNT_DELETE]: undefined;
  [AppRoute.ACCOUNT_DETAIL]: AccountCreateParams;
  [AppRoute.ACCOUNT_COLLECT_MISSING_DETAILS]: undefined;
  [AppRoute.ACCOUNT_CHANGE_PASSWORD]: AccountCreateParams;
  [AppRoute.ACCOUNT_REWARDS]: undefined;
  [AppRoute.ACCOUNT_COMMUNICATIONS_PREFERENCES]: undefined;
  [AppRoute.ACCOUNT_PASSWORD_RESET]: AccountPasswordResetParams;
  [AppRoute.ACCOUNT_RECENT_ORDERS]: undefined;
  [AppRoute.ACCOUNT_ORDER_VIEW]: AccountOrderViewParams;
  [AppRoute.ACCOUNT_PAYMENT_METHOD]: undefined;

  /**
   * Account flow - phone verification
   */
  [AppRoute.ACCOUNT_PHONE_VERIFICATION]: AccountPhoneVerificationParams;

  /**
   * Account flow - email verification on log in to existing account
   */
  [AppRoute.ACCOUNT_EMAIL_VERIFICATION]: undefined;

  /**
   * Account flow - success
   */
  [AppRoute.ACCOUNT_SUCCESS]: undefined;

  [AppRoute.GIFT_CARDS_OPTIONS]: undefined;
  [AppRoute.GIFT_CARDS_PARTICIPATING_STORES]: undefined;
  [AppRoute.GIFT_CARDS_PROMOTION_WAITER]: undefined;
  [AppRoute.GIFT_CARDS_CHECK_BALANCE]: undefined;
  [AppRoute.GIFT_CARDS_HOW_TO_REDEEM]: undefined;
  [AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS]: undefined;

  // Gift Cards Store
  [AppRoute.GIFT_CARDS_MAIN]: NavigatorScreenParams<GiftCardShopStackParamList>;

  // Rewards
  [AppRoute.REWARDS_SIGN_IN]: {staff?: string; signUpCode?: string; lmgCode?: string} | undefined;
  [AppRoute.REWARDS_STAFF_STORE_SELECTION]: RewardsSignUpTypeParams | undefined;
  [AppRoute.REWARDS_LMG_STAFF_SELECTION]: RewardsSignUpTypeParams | undefined;
  [AppRoute.REWARDS_SIGN_UP]: RewardsSignUpTypeParams | undefined;
  [AppRoute.REWARDS_SUCCESS]: undefined;
  [AppRoute.REWARDS_STAFF_ACTIVATION_BLOCKED]: {conflictKind?: StaffConflictKind} | undefined;
  [AppRoute.REWARDS_ACCOUNT_NOT_FOUND]: undefined;
  [AppRoute.REWARDS_TERMS_AND_CONDITIONS]: undefined;
  [AppRoute.REWARDS_VERIFICATION]: RewardsSignUpTypeParams | undefined;
  [AppRoute.REWARDS_FULL_SIGN_UP]: RewardsFullSignUpParams;
  [AppRoute.REWARDS_ACTIVATE_TERMS_AND_CONDITIONS]: RewardsActivateTermsAndConditionsParams;
  [AppRoute.REWARDS_ACTIVATE_VERIFICATION]: RewardsActivateVerificationParams;
  [AppRoute.REWARDS_ACTIVATE_SUCCESS]: RewardsActivateSuccessParams;

  // Storybook
  [AppRoute.STORYBOOK]: undefined;

  /**
   * Developer settings
   */
  [AppRoute.DEVELOPER_SETTINGS]: undefined;

  // TMP
  [AppRoute.SUMMER_CASH_GRAB_GAME]: PromotionIframeParams;
};

// AppRoutesNavigator == BottomTabsNavigator -> unused (no navigation/route used)
export type AppRoutesNavigatorScreenProps = CompositeScreenProps<
  DrawerScreenProps<DrawerParamList, AppRoute.MAIN>,
  CompositeScreenProps<
    | BottomTabScreenProps<AppRoutesNavigatorParamList>
    | StackScreenProps<AppRoutesNavigatorParamList>,
    StackScreenProps<RootStackParamList>
  >
>;

// Route screen stack navigator
export type RouteNavigatorScreenProps<RouteT extends keyof AppRoutesNavigatorParamList> =
  CompositeScreenProps<
    StackScreenProps<AppRoutesNavigatorParamList, RouteT>,
    CompositeScreenProps<
      | BottomTabScreenProps<AppRoutesNavigatorParamList>
      | StackScreenProps<AppRoutesNavigatorParamList>,
      CompositeScreenProps<DrawerScreenProps<DrawerParamList>, StackScreenProps<RootStackParamList>>
    >
  >;

type AccountLoginParams = {
  email: string;
};

type AccountPhoneVerificationParams =
  | undefined
  | {
      action: AccountScreenAction;
    };

type PromotionIframeParams = {
  iFrameLinkParams?: string;
  url: string;
};

export enum AccountScreenAction {
  UNKNOWN = 'Unknown',
  CREATE = 'Create',
  UPDATE = 'Update',
  CHANGE_PASSWORD = 'Change Password',
  RESET_PASSWORD = 'Reset Password',
  COLLECT_MISSING_DETAILS = 'Collect Missing Details',
  UPDATE_BILLING_ADDRESS = 'Update Billing Address',
  CHECKOUT_UPDATE_BILLING_ADDRESS = 'Checkout Update Billing Address',
}

export function parseAccountScreenActionKey(
  value?: string,
): keyof typeof AccountScreenAction | undefined {
  return isAccountScreenActionKey(value) ? value : getAccountScreenActionKey(value);
}

export function parseAccountScreenAction(value?: string): AccountScreenAction | undefined {
  const key = parseAccountScreenActionKey(value);
  return key ? AccountScreenAction[key] : undefined;
}

/**
 * Validates if a given value is a key of the AccountScreenAction enum
 * @param value The value to check
 * @returns true if the value is a valid key of AccountScreenAction, false otherwise
 */
export function isAccountScreenActionKey(
  value: string | undefined,
): value is keyof typeof AccountScreenAction {
  if (typeof value !== 'string') {
    return false;
  }

  return Object.keys(AccountScreenAction).includes(value);
}

/**
 * Gets the enum key from a string value in the AccountScreenAction enum
 * @param value The string value from the enum
 * @returns The corresponding enum key or undefined if not found
 */
export function getAccountScreenActionKey(
  value?: string,
): keyof typeof AccountScreenAction | undefined {
  const entries = Object.entries(AccountScreenAction) as [
    keyof typeof AccountScreenAction,
    string,
  ][];
  const entry = entries.find(([_, val]) => val === value);
  return entry ? entry[0] : undefined;
}

type AccountCreateParams = {
  email?: string;
  action: AccountScreenAction;
};

export enum InformationContentType {
  ABOUT_US = 'about-us',
  RETURNS_AND_EXCHANGES = 'returns-and-exchanges',
  DELIVERY_AND_PICKUP_FAQS = 'delivery-and-pickup-faqs',
  TERMS_AND_CONDITIONS = 'terms-and-conditions',
  REWARDS_PROGRAM_TERMS_AND_CONDITIONS = 'rewards-program-terms-and-conditions',
  PRIVACY_POLICY = 'privacy-policy',
}

type InformationContentParams = {
  target: InformationContentType;
};

type AccountOrderViewParams = {
  orderNumber: string;
};

// DIS-1339: optional pre-fill for the support ticket form. Carried as in-memory
// navigation params on every platform (web + native iOS/Android), so prefill
// behaves identically; on web it also keeps PII (phone/email) out of the URL
// (native has no address bar). All fields optional; the screen merges these over
// store-derived defaults.
export type TicketPrefill = {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  nameOfStore?: string;
  enquiryType?: string;
  description?: string;
};

type AccountPasswordResetParams = {
  token: string;
};

type IFrameFullScreen3DSParams = {
  url: string;
  navigationTarget:
    | AppRoute.ACCOUNT_PAYMENT_METHOD
    | AppRoute.CART_ORDER_CHECKOUT
    | AppRoute.CART_ORDER_PROCESSING;
};

type StoreSwitchLinkParams = {
  shopSalesforceId: string;
};

type ArticleDetailParams = {
  // Stub returned by linking.ts parse.article on cold-start deep links when
  // discoverList is empty; resolved to AirtableFeedItem on screen mount (DIS-1201).
  article: AirtableFeedItem | AirtableFeedItemStub;
};

type ShopBrandParams = {
  brand: AirtableBrand;
};

type ProductParams = {
  product?: OrderLine | string; // string for product_id on links :/
};

type PromotionDetailParams = {
  orderNumber?: string;
  promotion?: AirtablePromotionItem;
  orderId?: string;
  brand?: AirtableBrand;
};

type ExclusiveOfferDetailParams = {
  /**
   * The offer's contentId (Airtable id), which every offer has. DIS-1661 moved
   * offer identity off mflId because an offer can arrive without one. Deep
   * links published before that change carry an MFL id in this slot, so the
   * detail screen matches the param against either field.
   */
  offerId?: string;
};

type MembersPriceListParams = {
  external_sell_type: 'members-price' | 'just-for-you' | 'member-specials';
};

type PromotionsWinnersDetailParams = {
  promotion: AirtablePromotionItem;
};

type CompetitionOptOutParams = {
  competitionCode?: string;
  competitionName?: string;
};

type DepartmentParams = {
  department: Department;
  specials?: boolean;
};

// `join` is optional: the linking parse returns undefined for an unknown join id.
export type ShopAllParams = {
  join?: DepartmentJoinId;
  specials?: boolean;
};

type SubDepartmentParams = {
  department: Department;
  subDepartment?: Department;
};

type RewardsFullSignUpParams = {
  firstName: string;
};

export type RewardsSignUpTypeParams = {
  flowType: AccountStatus;
  /**
   * DIS-1185: when set to 'verification-expired', RewardsVerificationScreen
   * shows a banner explaining why the user landed back on this screen
   * (e.g., previous OTP attempt failed validation/server error).
   */
  returnReason?: 'verification-expired';
};

type RewardsActivateTermsAndConditionsParams = {
  /**
   * Rewards activation flow converge here from "My Account" or "Log me in".
   * If from My account flow return back to "My Account" > "My rewards" and if from "Log Me In" show success page
   */
  activateFlow?: 'logMeIn' | 'activateRewards';
};

type RewardsActivateVerificationParams = {
  /**
   * Rewards activation flow converge here from "My Account" or "Log me in".
   * If from My account flow return back to "My Account" > "My rewards" and if from "Log Me In" show success page
   */
  activateFlow?: 'logMeIn';
};

type RewardsActivateSuccessParams = {
  /**
   * The success screen is shown from lite sign up flow or from activate rewards log me in flow. Use the flow param to differentiate
   */
  flow?: 'activate';
};

// Utility types
export type UseAppNavigationParamList = BottomTabNavigationProp<AppRoutesNavigatorParamList>;

// Utils
export type HeaderConfig = {
  back: boolean;
  shop?: boolean;
  filter: boolean | string;
  burger: boolean;
  search: boolean;
  source?: string;

  /**
   * Remove distractions from header. just keep the logo
   */
  blinkerMode?: boolean;

  /**
   * Drop the second header row — the one holding back/shop, the title and the
   * filter icon — while keeping the primary row and, on web desktop, the top
   * navigation. For a screen that draws its own back or close control: with
   * every slot in that row empty it renders as a 40px strip with a grey bottom
   * border and nothing in it. `blinkerMode` also hides it, but takes the
   * burger, user and search icons with it.
   */
  hideDynamicHeader?: boolean;
};
